import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { logger } from '../lib/logger';

export class ErrorBoundary extends React.Component<{children?: React.ReactNode}, {hasError: boolean, error?: Error}> {
  constructor(props: {children?: React.ReactNode}) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.critical('REACTIVE_CRASH', error.message, { 
      stack: error.stack,
      componentStack: errorInfo.componentStack 
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center p-6">
          <div className="max-w-md w-full bg-white dark:bg-slate-900 rounded-[2.5rem] p-10 shadow-2xl shadow-blue-500/10 border border-slate-200 dark:border-slate-800 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-3xl flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
            </div>
            <h1 className="text-2xl font-black mb-3">Something went wrong</h1>
            <p className="text-slate-500 dark:text-slate-400 font-bold mb-8 leading-relaxed">
              We encountered an unexpected error while loading this page.
            </p>
            <div className="space-y-3">
              <button 
                onClick={() => window.location.reload()}
                className="w-full py-4 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-2xl font-black flex items-center justify-center gap-2 hover:opacity-90 transition-all active:scale-95"
              >
                <RefreshCw className="w-5 h-5" />
                Reload Application
              </button>
              <button 
                onClick={() => this.setState({ hasError: false })}
                className="w-full py-4 bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-2xl font-black hover:bg-slate-200 dark:hover:bg-slate-700 transition-all"
              >
                Try to resume
              </button>
            </div>
            {process.env.NODE_ENV !== 'production' && (
              <div className="mt-8 text-left p-4 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-100 dark:border-red-900/20 overflow-auto max-h-40">
                <code className="text-xs font-mono text-red-600 dark:text-red-400 whitespace-pre">
                  {this.state.error?.stack}
                </code>
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
